import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Container,
  Flex,
  Grid,
  HStack,
  VStack,
  Text,
  Heading,
  Badge,
  Button,
  Icon,
  Image,
  Spinner,
  Divider,
  Tooltip,
  useToast,
} from "@chakra-ui/react";
import {
  IoStorefrontOutline,
  IoPeopleOutline,
  IoLocationOutline,
  IoResizeOutline,
  IoPricetagOutline,
  IoTimeOutline,
  IoAddOutline,
  IoCheckmarkCircleOutline,
  IoEllipseOutline,
  IoAlertCircleOutline,
  IoCloseCircleOutline,
  IoCreateOutline,
  IoImagesOutline,
} from "react-icons/io5";
import { motion } from "framer-motion";
import { useSearchParams } from "react-router-dom";
import { collection, query, where, getDocs, orderBy, doc, getDoc } from "firebase/firestore";
import { useAuthState } from "react-firebase-hooks/auth";
import { auth, db } from "../firebase/firebase-config";
import { useDisclosure } from "@chakra-ui/react";
import { VenueOnboardingModal, VenueAgreementModal, VenuePayoutModal } from "../Components/Modals";
import {
  isVenueAgreementSigned,
  hasPayoutsReady,
  hasVerifiedStripeAccount,
} from "../lib/venueAgreement";

const MotionBox = motion(Box);

// ─── Status config ────────────────────────────────────────────────────────────
const STATUS_CONFIG = {
  pending_review: { label: "Under Review", color: "orange", hex: "#f97316", icon: IoTimeOutline },
  approved:       { label: "Approved",     color: "blue",   hex: "#508cf7", icon: IoCheckmarkCircleOutline },
  active:         { label: "Active",       color: "green",  hex: "#22c55e", icon: IoEllipseOutline },
  inactive:       { label: "Inactive",     color: "gray",   hex: "#9ca3af", icon: IoEllipseOutline },
  rejected:       { label: "Rejected",     color: "red",    hex: "#ef4444", icon: IoCloseCircleOutline },
};

const ALL_STATUSES = Object.entries(STATUS_CONFIG).map(([value, cfg]) => ({ value, ...cfg }));

// `active: true` overrides the textual review status for display purposes.
// This lets the admin flip a single boolean in Firestore to make a venue live.
const getEffectiveStatus = (v) => {
  if (v?.active === true) return "active";
  return v?.status || "pending_review";
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
const fmt = (n, prefix = "") =>
  n != null ? `${prefix}${Number(n).toLocaleString()}` : "—";

const fmtDate = (ts) => {
  if (!ts) return "—";
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleDateString("en-CA", { year: "numeric", month: "short", day: "numeric" });
};

// ─── Venue card ───────────────────────────────────────────────────────────────
const VenueCard = ({ venue, index, onEdit }) => {
  const cfg = STATUS_CONFIG[getEffectiveStatus(venue)] || STATUS_CONFIG.pending_review;
  const images = venue.images || [];

  return (
    <MotionBox
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, delay: index * 0.07 }}
      bg="white"
      borderRadius="2xl"
      overflow="hidden"
      border="1px solid"
      borderColor="gray.100"
      boxShadow="0 1px 4px rgba(0,0,0,0.04)"
      _hover={{
        borderColor: "blue.100",
        boxShadow: "0 6px 24px rgba(80,140,247,0.10)",
        transform: "translateY(-3px)",
      }}
      style={{ transition: "all 0.22s ease" }}
    >
      {/* Image strip / placeholder */}
      {images.length > 0 ? (
        <HStack
          spacing={0}
          h="120px"
          overflowX="auto"
          css={{
            "&::-webkit-scrollbar": { display: "none" },
            scrollbarWidth: "none",
          }}
        >
          {images.map((img, i) => (
            <Box
              key={img.url}
              flexShrink={0}
              w={images.length === 1 ? "full" : "55%"}
              h="full"
              borderRight={i < images.length - 1 ? "1px solid white" : "none"}
            >
              <Image src={img.url} alt={`Venue ${i + 1}`} w="full" h="full" objectFit="cover" />
            </Box>
          ))}
        </HStack>
      ) : (
        <Flex h="72px" bg="gray.50" align="center" justify="center" borderBottom="1px solid" borderColor="gray.100">
          <HStack spacing={2} color="gray.300">
            <Icon as={IoImagesOutline} boxSize={4} />
            <Text fontSize="11px" fontWeight="500">No photos</Text>
          </HStack>
        </Flex>
      )}

      <Flex>
        <Box w="3px" bg={cfg.hex} flexShrink={0} />

        <Box flex="1" p={{ base: 4, md: 6 }} minW={0}>
          {/* Top row */}
          <Flex justify="space-between" align="flex-start" mb={3}>
            <VStack align="flex-start" spacing="2px">
              <Text
                fontSize="10px"
                fontWeight="600"
                color="gray.400"
                textTransform="uppercase"
                letterSpacing="0.08em"
              >
                {venue.venueType || "Venue"}
              </Text>
              <Heading
                fontSize="lg"
                fontWeight="700"
                color="gray.800"
                lineHeight="1.25"
                letterSpacing="-0.01em"
              >
                {venue.venueName}
              </Heading>
            </VStack>

            <HStack
              spacing={1}
              bg={`${cfg.color}.50`}
              color={`${cfg.color}.600`}
              borderRadius="full"
              px={2}
              py={1}
              flexShrink={0}
              ml={3}
            >
              <Icon as={cfg.icon} boxSize="10px" />
              <Text fontSize="11px" fontWeight="600">{cfg.label}</Text>
            </HStack>
          </Flex>

          {/* Description */}
          {venue.description && (
          <Text fontSize="sm" color="gray.500" noOfLines={2} lineHeight="1.6" mb={4} textAlign="left">
            {venue.description}
          </Text>
          )}

          {/* Location */}
          {(venue.city || venue.streetAddress) && (
            <HStack spacing="6px" mb={5}>
              <Icon as={IoLocationOutline} color="gray.300" boxSize="13px" flexShrink={0} />
              <Text fontSize="12px" color="gray.400">
                {[venue.streetAddress, venue.city, venue.province].filter(Boolean).join(", ")}
              </Text>
            </HStack>
          )}

          {/* Stat row */}
          <HStack
            spacing={0}
            bg="gray.50"
            borderRadius="xl"
            overflow="hidden"
            divider={<Box w="1px" h="full" bg="gray.100" flexShrink={0} />}
            mb={5}
          >
            {[
              { icon: IoPeopleOutline, label: "Capacity", value: fmt(venue.maxCapacity) },
              { icon: IoResizeOutline,  label: "Sq Ft",    value: fmt(venue.squareFootage) },
              { icon: IoPricetagOutline, label: "Min / hr", value: fmt(venue.minPricePerHour, "$") },
            ].map(({ icon, label, value }) => (
              <VStack key={label} flex="1" spacing="1px" py={3} px={2} align="center">
                <Icon as={icon} color="gray.400" boxSize="13px" />
                <Text fontSize="9px" color="gray.400" textTransform="uppercase" letterSpacing="0.06em" fontWeight="600">
                  {label}
                </Text>
                <Text fontSize="13px" fontWeight="700" color="gray.700">{value}</Text>
              </VStack>
            ))}
          </HStack>

          {/* Footer */}
          <Flex
            justify="space-between"
            align={{ base: "flex-start", sm: "center" }}
            gap={3}
            flexWrap="wrap"
          >
            <VStack spacing={0} align="flex-start" minW={0}>
              <Text fontSize="11px" color="gray.300">
                Submitted {fmtDate(venue.createdAt)}
              </Text>
              <Text fontSize="10px" color="gray.300" fontWeight="500">
                #{venue.id.slice(0, 6).toUpperCase()}
              </Text>
            </VStack>
            <Button
              size="sm"
              variant="outline"
              color="#508cf7"
              borderColor="blue.100"
              bg="white"
              fontWeight="600"
              fontSize="12px"
              borderRadius="full"
              h="32px"
              px={3}
              leftIcon={<Icon as={IoCreateOutline} boxSize="14px" />}
              _hover={{
                bg: "blue.50",
                borderColor: "#508cf7",
                transform: "translateY(-1px)",
                boxShadow: "0 4px 10px rgba(80,140,247,0.18)",
              }}
              _active={{ transform: "translateY(0)" }}
              style={{ transition: "all 0.18s ease" }}
              onClick={(e) => {
                e.stopPropagation();
                onEdit(venue);
              }}
            >
              Edit
            </Button>
          </Flex>
        </Box>
      </Flex>
    </MotionBox>
  );
};

// ─── Page ─────────────────────────────────────────────────────────────────────
export const VenuesPage = () => {
  const [user] = useAuthState(auth);
  const [searchParams, setSearchParams] = useSearchParams();
  const [venues, setVenues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState("all");
  const [editingVenue, setEditingVenue] = useState(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isAgreementOpen, onOpen: onAgreementOpen, onClose: onAgreementClose } = useDisclosure();
  const { isOpen: isPayoutOpen, onOpen: onPayoutOpen, onClose: onPayoutClose } = useDisclosure();
  // null = not checked yet — we never flash a step someone has already
  // completed while the Firestore read is in flight.
  const [agreementSigned, setAgreementSigned] = useState(null);
  const [payoutsReady, setPayoutsReady] = useState(null);
  // Already finished Stripe in another role (i.e. as a host) — they get a
  // "you're already connected" note instead of a second trip to Stripe.
  const [stripeConnected, setStripeConnected] = useState(false);
  const [pendingNewVenue, setPendingNewVenue] = useState(false);
  const toast = useToast();

  useEffect(() => {
    if (!user) {
      setAgreementSigned(false);
      setPayoutsReady(false);
      setStripeConnected(false);
      return;
    }
    let cancelled = false;
    getDoc(doc(db, "users", user.uid))
      .then((snap) => {
        if (cancelled) return;
        const data = snap.exists() ? snap.data() : null;
        setAgreementSigned(isVenueAgreementSigned(data));
        setPayoutsReady(hasPayoutsReady(data));
        setStripeConnected(hasVerifiedStripeAccount(data));
      })
      .catch((err) => {
        console.error("Error checking venue partner status:", err);
        if (!cancelled) {
          setAgreementSigned(false);
          setPayoutsReady(false);
          setStripeConnected(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [user]);

  const handleEdit = (venue) => {
    setEditingVenue(venue);
    onOpen();
  };

  // New venues require the Venue Platform and Event Hosting Agreement and
  // Stripe payouts — no need to be an event host. Editing an existing venue
  // doesn't need to re-check either.
  const advanceNewVenueFlow = () => {
    if (!agreementSigned) {
      onAgreementOpen();
      return;
    }
    if (!payoutsReady) {
      onPayoutOpen();
      return;
    }
    onOpen();
  };

  const handleNewVenue = () => {
    setEditingVenue(null);
    if (agreementSigned === null || payoutsReady === null) {
      // Still loading the Firestore read — park the intent, the effect
      // below picks it back up as soon as it resolves.
      setPendingNewVenue(true);
      return;
    }
    advanceNewVenueFlow();
  };

  useEffect(() => {
    if (pendingNewVenue && agreementSigned !== null && payoutsReady !== null) {
      setPendingNewVenue(false);
      advanceNewVenueFlow();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingNewVenue, agreementSigned, payoutsReady]);

  // Coming back from Stripe payout onboarding (see HostOnboarding.js) —
  // pick the listing flow back up where it left off.
  useEffect(() => {
    if (searchParams.get("openVenue") === "true" && user) {
      setEditingVenue(null);
      setPendingNewVenue(true);
      setSearchParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, user]);

  const handleModalClose = () => {
    onClose();
    setEditingVenue(null);
    fetchVenues();
  };

  const fetchVenues = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const q = query(
        collection(db, "venues"),
        where("userId", "==", user.uid),
        orderBy("createdAt", "desc")
      );
      const snap = await getDocs(q);
      setVenues(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (err) {
      toast({ title: "Failed to load venues", description: err.message, status: "error", duration: 4000, isClosable: true });
    } finally {
      setLoading(false);
    }
  }, [user, toast]);

  useEffect(() => { fetchVenues(); }, [fetchVenues]);

  const filtered = activeFilter === "all"
    ? venues
    : venues.filter((v) => getEffectiveStatus(v) === activeFilter);

  const counts = Object.fromEntries(
    Object.keys(STATUS_CONFIG).map((s) => [
      s,
      venues.filter((v) => getEffectiveStatus(v) === s).length,
    ])
  );

  return (
    <Box minH="100vh" bg="#f7f8fc">

      {/* ── Page header ── */}
      <Box bg="white" borderBottom="1px solid" borderColor="gray.100" px={4} py={4}>
        <Container maxW="5xl">
          <Flex justify="space-between" align="center" gap={4} flexWrap="wrap">
            <HStack spacing={3} align="center">
              <Box
                w="32px"
                h="32px"
                borderRadius="lg"
                bg="blue.50"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <Icon as={IoStorefrontOutline} boxSize="15px" color="#508cf7" />
              </Box>
              <VStack align="flex-start" spacing={0}>
                <Heading
                  fontSize={{ base: "lg", md: "xl" }}
                  fontWeight="700"
                  color="gray.800"
                  letterSpacing="-0.01em"
                  lineHeight="1.2"
                >
                  Your Venues
                </Heading>
                <Text fontSize="12px" color="gray.400" lineHeight="1.3">
                  Track and manage your venue applications
                </Text>
              </VStack>

              {/* Compact inline stats */}
              {!loading && venues.length > 0 && (
                <HStack
                  spacing={1.5}
                  ml={4}
                  pl={4}
                  borderLeft="1px solid"
                  borderColor="gray.100"
                  display={{ base: "none", md: "flex" }}
                >
                  {[
                    { label: "Total",       value: venues.length,         color: "gray.700" },
                    { label: "Under Review",value: counts.pending_review, color: "orange.500" },
                    { label: "Approved",    value: counts.approved,       color: "#508cf7" },
                    { label: "Active",      value: counts.active,         color: "green.500" },
                  ].map(({ label, value, color }) => (
                    <HStack
                      key={label}
                      spacing={1.5}
                      px={2.5}
                      py={1}
                      bg="gray.50"
                      borderRadius="full"
                    >
                      <Text fontSize="11px" color="gray.400" fontWeight="500">{label}</Text>
                      <Text fontSize="12px" fontWeight="700" color={color}>{value}</Text>
                    </HStack>
                  ))}
                </HStack>
              )}
            </HStack>

            <Button
              leftIcon={<Icon as={IoAddOutline} boxSize={4} />}
              bg="#508cf7"
              color="white"
              fontWeight="600"
              fontSize="sm"
              borderRadius="xl"
              size="sm"
              px={4}
              _hover={{ bg: "#226cf1", transform: "translateY(-1px)", boxShadow: "0 4px 12px rgba(80,140,247,0.35)" }}
              style={{ transition: "all 0.18s ease" }}
              onClick={handleNewVenue}
            >
              New Venue
            </Button>
          </Flex>

          {/* Mobile-only compact stats */}
          {!loading && venues.length > 0 && (
            <HStack
              spacing={1.5}
              mt={3}
              flexWrap="wrap"
              display={{ base: "flex", md: "none" }}
            >
              {[
                { label: "Total",       value: venues.length,         color: "gray.700" },
                { label: "Under Review",value: counts.pending_review, color: "orange.500" },
                { label: "Approved",    value: counts.approved,       color: "#508cf7" },
                { label: "Active",      value: counts.active,         color: "green.500" },
              ].map(({ label, value, color }) => (
                <HStack key={label} spacing={1.5} px={2.5} py={1} bg="gray.50" borderRadius="full">
                  <Text fontSize="11px" color="gray.400" fontWeight="500">{label}</Text>
                  <Text fontSize="12px" fontWeight="700" color={color}>{value}</Text>
                </HStack>
              ))}
            </HStack>
          )}
        </Container>
      </Box>

      {/* ── Content ── */}
      <Container maxW="5xl" pt={5} pb={16} px={{ base: 3, md: 4 }}>

        {/* Filter pills */}
        {venues.length > 0 && (
          <HStack spacing={2} mb={7} flexWrap="wrap">
            <Button
              size="sm"
              borderRadius="full"
              fontWeight="500"
              fontSize="12px"
              px={4}
              bg={activeFilter === "all" ? "#508cf7" : "white"}
              color={activeFilter === "all" ? "white" : "gray.500"}
              border="1px solid"
              borderColor={activeFilter === "all" ? "#508cf7" : "gray.200"}
              _hover={{ borderColor: "#508cf7", color: activeFilter === "all" ? "white" : "#508cf7" }}
              onClick={() => setActiveFilter("all")}
            >
              All  {venues.length}
            </Button>
            {ALL_STATUSES.filter((s) => counts[s.value] > 0).map((s) => (
              <Button
                key={s.value}
                size="sm"
                borderRadius="full"
                fontWeight="500"
                fontSize="12px"
                px={4}
                bg={activeFilter === s.value ? `${s.color}.50` : "white"}
                color={activeFilter === s.value ? `${s.color}.600` : "gray.500"}
                border="1px solid"
                borderColor={activeFilter === s.value ? `${s.color}.200` : "gray.200"}
                _hover={{ borderColor: `${s.color}.300`, color: `${s.color}.600` }}
                onClick={() => setActiveFilter(s.value)}
              >
                {s.label}  {counts[s.value]}
              </Button>
            ))}
          </HStack>
        )}

        {/* Loading */}
        {loading && (
          <Flex justify="center" align="center" py={28}>
            <VStack spacing={3}>
              <Spinner size="md" color="#508cf7" thickness="2px" />
              <Text color="gray.300" fontSize="sm" fontWeight="400">Loading…</Text>
            </VStack>
          </Flex>
        )}

        {/* Empty state */}
        {!loading && venues.length === 0 && (
          <MotionBox
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
          >
            <Flex
              direction="column"
              align="flex-start"
              py={{ base: 10, md: 16 }}
              px={{ base: 5, md: 8 }}
              bg="white"
              borderRadius="2xl"
              border="1px dashed"
              borderColor="gray.200"
            >
              <Box
                w="56px" h="56px"
                borderRadius="xl"
                bg="blue.50"
                display="flex"
                alignItems="center"
                justifyContent="center"
                mb={5}
              >
                <Icon as={IoStorefrontOutline} boxSize={6} color="#508cf7" />
              </Box>
              <Text fontWeight="600" fontSize="md" color="gray.700" mb={1}>
                No venues yet
              </Text>
              <Text color="gray.400" fontSize="sm" mb={6} textAlign="left" maxW="320px" lineHeight="1.6">
                Submit your first venue to get it listed on Proximy.
              </Text>
              <Button
                leftIcon={<Icon as={IoAddOutline} />}
                bg="#508cf7"
                color="white"
                borderRadius="xl"
                fontWeight="600"
                size="sm"
                _hover={{ bg: "#226cf1" }}
                onClick={handleNewVenue}
              >
                Add Your First Venue
              </Button>
            </Flex>
          </MotionBox>
        )}

        {/* Filtered empty */}
        {!loading && venues.length > 0 && filtered.length === 0 && (
          <Flex justify="flex-start" align="center" py={{ base: 10, md: 16 }}>
            <HStack spacing={3} align="center">
              <Icon as={IoAlertCircleOutline} boxSize={6} color="gray.200" />
              <Text color="gray.400" fontSize="sm">No venues with this status.</Text>
            </HStack>
          </Flex>
        )}

        {/* Venue grid */}
        {!loading && filtered.length > 0 && (
          <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }} gap={{ base: 3, md: 4 }}>
            {filtered.map((venue, i) => (
              <VenueCard key={venue.id} venue={venue} index={i} onEdit={handleEdit} />
            ))}
          </Grid>
        )}
      </Container>

      <VenueAgreementModal
        isOpen={isAgreementOpen}
        onClose={onAgreementClose}
        user={user}
        onSigned={() => {
          setAgreementSigned(true);
          onAgreementClose();
          // Payouts are the only thing left between them and the form.
          if (payoutsReady) onOpen();
          else onPayoutOpen();
        }}
      />

      <VenuePayoutModal
        isOpen={isPayoutOpen}
        onClose={onPayoutClose}
        user={user}
        returnPath="/venues"
        alreadyConnected={stripeConnected}
        onLinked={() => {
          setPayoutsReady(true);
          onPayoutClose();
          onOpen();
        }}
      />

      <VenueOnboardingModal
        isOpen={isOpen}
        onClose={handleModalClose}
        user={user}
        venue={editingVenue}
      />
    </Box>
  );
};
